import NetInfo from '@/adapters/netinfo';
import React, {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { AppState, AppStateStatus } from 'react-native';
import { NetInfoStateType } from '@react-native-community/netinfo';
import { OfflineModeSimulator } from '@/utils/offline-mode/offline-mode-simulator';
import type { NetInfoState } from '@react-native-community/netinfo';

export enum NetworkStatus {
  CONNECTED,
  DISCONNECTED,
  UNDETERMINED,
}

type NetworkEffectiveType = '2g' | '3g' | '4g' | '5g' | null;

export interface NetworkDetail {
  networkEffectiveType: NetworkEffectiveType;
  networkType: NetInfoState['type'];
}

let _networkDetail: NetworkDetail = {
  networkEffectiveType: null,
  networkType: NetInfoStateType.unknown,
};

export const getNetworkDetail = (): NetworkDetail => _networkDetail;

const updateNetworkDetail = (state: NetInfoState) => {
  _networkDetail = {
    networkEffectiveType:
      state.type === 'cellular'
        ? (state.details?.cellularGeneration ?? null)
        : null,
    networkType: state.type,
  };
};

export interface NetworkStatusContextType {
  networkStatus: NetworkStatus;
}

interface Props {
  children?: React.ReactNode;
}

export const NetworkStatusProvider: React.FC<Props> = ({ children }) => {
  const offlineModeSimulatorEnabled =
    OfflineModeSimulator.getInstance().checkEnabled();
  const [isOfflineModeSimulated, setIsOfflineModeSimulated] = useState(
    offlineModeSimulatorEnabled,
  );
  const [networkStatus, setNetworkStatus] = useState(
    NetworkStatus.UNDETERMINED,
  );
  const derivedNetworkStatus = isOfflineModeSimulated
    ? NetworkStatus.DISCONNECTED
    : networkStatus;

  const checkNetworkStatus = async () => {
    const state = await NetInfo.fetch();
    if (state.isInternetReachable === null || state.isConnected === null) {
      setNetworkStatus(NetworkStatus.UNDETERMINED);
    } else if (state.isConnected && state.isInternetReachable) {
      setNetworkStatus(NetworkStatus.CONNECTED);
    } else {
      setNetworkStatus(NetworkStatus.DISCONNECTED);
    }
  };

  useEffect(() => {
    const offlineModeSimulator = OfflineModeSimulator.getInstance();
    const listenerId = offlineModeSimulator.addEventListener((isEnabled) => {
      setIsOfflineModeSimulated(isEnabled);
    });

    return () => {
      offlineModeSimulator.removeEventListener(listenerId);
    };
  }, []);

  useEffect(() => {
    const handleAppStateChange = async (nextAppState: AppStateStatus) => {
      if (nextAppState === 'active') {
        await checkNetworkStatus();
      }
    };

    const unsubscribeNetInfo = NetInfo.addEventListener(async (state) => {
      updateNetworkDetail(state);
      if (state.isInternetReachable === null || state.isConnected === null) {
        setNetworkStatus(NetworkStatus.UNDETERMINED);
      } else if (state.isConnected && state.isInternetReachable) {
        setNetworkStatus(NetworkStatus.CONNECTED);
      } else {
        setNetworkStatus(NetworkStatus.DISCONNECTED);
      }
    });

    const subscriptionAppState = AppState.addEventListener(
      'change',
      handleAppStateChange,
    );

    // Initial network status check
    checkNetworkStatus();

    return () => {
      unsubscribeNetInfo();
      subscriptionAppState.remove();
    };
  }, []);

  const value = useMemo(
    () => ({ networkStatus: derivedNetworkStatus }),
    [derivedNetworkStatus],
  );

  return (
    <NetworkStatusContext.Provider value={value}>
      {children}
    </NetworkStatusContext.Provider>
  );
};

export const NetworkStatusContext =
  createContext<NetworkStatusContextType | null>(null);

export const useNetworkStatus = () => {
  const context = useContext(NetworkStatusContext);

  if (!context) {
    throw new Error('useNetworkStatus requires a NetworkStatusProvider');
  }

  return context;
};
